import React, { useContext, useReducer } from 'react';
import PropTypes from 'prop-types';
import {
  NEXT_STEP,
  BACK_STEP,
  SET_LAST_VISITED_STEP,
  SET_HAS_SUBMITTED,
  UPDATE_INITIAL_VALUES,
  GO_STEP,
  SET_VALUES,
  SET_DEFAULT_STEPS,
  DEFAULT_STEPS as defaultSteps,
} from './constants';

const wizardReducer = (state, action) => {
  switch (action.type) {
    case SET_VALUES: {
      return {
        ...state,
        ...action.data,
      };
    }
    case SET_DEFAULT_STEPS: {
      return {
        ...state,
        defaultSteps: action.defaultSteps,
      };
    }
    case GO_STEP: {
      return {
        ...state,
        currentStep: action.step,
        isContinuable: !(action.step >= state.totalSteps),
        isBackable: action.step > 1,
        isLastPage: action.step === state.totalSteps,
        validationSchema: state.validationsSchemas,
        lastVisitedStep: action.lastVisitedStep ? action.lastVisitedStep : state.lastVisitedStep,
      };
    }
    case NEXT_STEP: {
      const currentStep = Math.min(state.currentStep + 1, state.totalSteps);
      return {
        ...state,
        currentStep,
        isContinuable: !(currentStep >= state.totalSteps),
        isBackable: currentStep > 1,
        isLastPage: currentStep === state.totalSteps,
        lastVisitedStep: Math.max(currentStep, state.lastVisitedStep),
        initialValues: action.values,
      };
    }
    case BACK_STEP: {
      const currentStep = Math.max(state.currentStep - 1, 0);
      return {
        ...state,
        currentStep,
        isContinuable: !(currentStep >= state.totalSteps),
        isBackable: currentStep > 1,
        isLastPage: currentStep === state.totalSteps,
      };
    }
    case SET_LAST_VISITED_STEP: {
      return {
        ...state,
        lastVisitedStep: state.currentStep,
      };
    }
    case SET_HAS_SUBMITTED: {
      return {
        ...state,
        hasSubmitted: action.payload,
        currentStep: action.payload ? state.totalSteps : 0,
        isLastPage: state.totalSteps === state.currentStep,
      };
    }
    case UPDATE_INITIAL_VALUES: {
      return {
        ...state,
        initialValues: action.initialValues,
        lastVisitedStep: state.totalSteps,
        lastVisitedSummaryStep: state.totalSteps,
        isEditForm: !!action.initialValues.illustrationId,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const WizardStateContext = React.createContext();
const WizardDispatchContext = React.createContext();

const defaultInitialState = {
  currentStep: 0,
  totalSteps: defaultSteps.length - 1,
  isContinuable: true,
  isBackable: false,
  initialValues: {},
  lastVisitedStep: 0,
  isLastPage: false,
  hasSubmitted: false,
  validationsSchemas: [],
  defaultSteps,
};

const WizardProvider = ({
  initialValues, submitOnStep, validationsSchema, children,
}) => {
  const [state, dispatch] = useReducer(wizardReducer, {
    ...defaultInitialState,
    initialValues: { ...initialValues },
    submitOnStep,
    validationsSchema,
  });

  return (
    <WizardStateContext.Provider value={state}>
      <WizardDispatchContext.Provider value={dispatch}>
        {children}
      </WizardDispatchContext.Provider>
    </WizardStateContext.Provider>
  );
};

WizardProvider.propTypes = {
  initialValues: PropTypes.shape(),
  submitOnStep: PropTypes.number,
  children: PropTypes.element,
  validationsSchema: PropTypes.oneOfType([PropTypes.any]),
};

WizardProvider.defaultProps = {
  initialValues: {},
  submitOnStep: defaultInitialState.totalSteps,
  children: {},
  validationsSchema: undefined,
};

// custom hook to create FormWizard context
const useWizardState = () => {
  const context = useContext(WizardStateContext);
  if (!context) {
    throw new Error('useWizardState must be used within a WizardProvider');
  }

  return context;
};

// custom hook to create FormWizard context
const useWizardDispatch = () => {
  const context = useContext(WizardDispatchContext);
  if (!context) {
    throw new Error('useWizardDispatch must be used within a WizardProvider');
  }
  return context;
};

const useWizard = () => [useWizardState(), useWizardDispatch()];

export {
  WizardProvider, useWizard, useWizardState, useWizardDispatch,
};
